import type { FilmProject, ProjectGoal, ProjectScriptUpload } from "../types";
import { goalLabel } from "../utils/analysisSummary";
import { clampRuntimeSeconds, formatRuntime } from "../utils/storyboard";
import { Icon } from "./Icon";

interface ProjectIntakeProps {
  project: FilmProject;
  scriptUpload?: ProjectScriptUpload;
  isAnalyzing: boolean;
  onProjectChange: (changes: Partial<FilmProject>) => void;
  onScriptUpload: (file: File) => void;
  onAnalyze: () => void;
  onOpenImport: () => void;
  onClose: () => void;
}

const goals: ProjectGoal[] = [
  "trailer",
  "teaser",
  "sizzle_reel",
  "proof_of_concept",
  "previz_animatic",
  "concept_scene",
  "short_film",
  "broll_establishing",
  "vfx_plate",
  "social_cutdown",
  "pitch_reel",
  "festival_investor"
];

const goalCopy: Record<ProjectGoal, string> = {
  trailer: "Choose the strongest beats and build a fast proof of tone.",
  teaser: "Short, mysterious, and hook-first with minimal dialogue.",
  sizzle_reel: "Sell the world, characters, and visual promise quickly.",
  proof_of_concept: "Prove one core scene can work before a bigger run.",
  previz_animatic: "Assemble clear boards and timing for director review.",
  concept_scene: "Focus on a single sequence with controllable variables.",
  short_film: "Cover the whole story with scene-by-scene boards.",
  broll_establishing: "Create atmosphere, locations, and transition plates.",
  vfx_plate: "Prioritize clean references, framing, and effects intent.",
  social_cutdown: "Build vertical or square cuts for fast audience testing.",
  pitch_reel: "Show the premise, lead character, and cinematic promise.",
  festival_investor: "Package the most polished version for stakeholder review."
};

const runtimeOptions = [
  { label: "0:15", value: 15 },
  { label: "0:30", value: 30 },
  { label: "1:00", value: 60 },
  { label: "2:00", value: 120 },
  { label: "5:00", value: 300 },
  { label: "10:00", value: 600 },
  { label: "15:00", value: 900 }
];

export function ProjectIntake({
  project,
  scriptUpload,
  isAnalyzing,
  onProjectChange,
  onScriptUpload,
  onAnalyze,
  onOpenImport,
  onClose
}: ProjectIntakeProps) {
  const recommendedScenes = project.goal === "trailer" || project.goal === "teaser" ? "AI selects only the strongest beats." : "AI maps the full script into editable scenes.";
  const recommendedBoards = project.goal === "short_film" ? "Full scene coverage" : project.goal === "previz_animatic" ? "Director timing boards" : "Pitch-ready storyboard frames";
  const runtimeSelectOptions = runtimeOptions.some((option) => option.value === project.runtimeSeconds)
    ? runtimeOptions
    : [{ label: `${formatRuntime(project.runtimeSeconds)} custom`, value: project.runtimeSeconds }, ...runtimeOptions];

  return (
    <div className="flow-view setup-view">
      <header className="flow-hero">
        <div>
          <span>Project Intake</span>
          <h1>Tell Kinetix what you are making.</h1>
          <p>Upload the script and optional notes. The app will parse the project, suggest a production goal, and prepare a storyboard plan before anything is generated.</p>
        </div>
        <div className="flow-actions">
          <button className="secondary-action" onClick={onClose} type="button">
            Skip setup
          </button>
          <button className="primary-action" disabled={isAnalyzing} onClick={onAnalyze} type="button">
            <Icon name="spark" />
            {isAnalyzing ? "Analyzing" : "Analyze Script"}
          </button>
        </div>
      </header>

      <section className="setup-grid">
        <article className="setup-card project-basics">
          <div className="card-heading">
            <span>Start Here</span>
            <h2>Project basics</h2>
          </div>
          <label>
            <span>Project name</span>
            <input
              value={project.title}
              onChange={(event) => onProjectChange({ title: event.target.value || "Untitled Film" })}
              placeholder="Project title"
            />
          </label>
          <label>
            <span>Target runtime</span>
            <div className="runtime-controls">
              <select value={project.runtimeSeconds} onChange={(event) => onProjectChange({ runtimeSeconds: Number(event.target.value) })}>
                {runtimeSelectOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <input
                aria-label="Custom target runtime seconds"
                min={1}
                onChange={(event) => onProjectChange({ runtimeSeconds: clampRuntimeSeconds(Number(event.target.value), project.runtimeSeconds) })}
                step={1}
                type="number"
                value={project.runtimeSeconds}
              />
            </div>
          </label>
          <label>
            <span>Aspect ratio</span>
            <select value={project.aspectRatio} onChange={(event) => onProjectChange({ aspectRatio: event.target.value as FilmProject["aspectRatio"] })}>
              <option value="16:9">16:9</option>
              <option value="2.39:1">2.39:1 letterbox</option>
              <option value="1:1">1:1</option>
              <option value="4:5">4:5</option>
              <option value="9:16">9:16 vertical</option>
            </select>
          </label>
          <label>
            <span>Test quality</span>
            <select value={project.resolution} onChange={(event) => onProjectChange({ resolution: event.target.value as FilmProject["resolution"] })}>
              <option value="480p">480p test</option>
              <option value="720p">720p review</option>
              <option value="1080p">1080p polish</option>
            </select>
          </label>
        </article>

        <article className="setup-card upload-card">
          <div className="card-heading">
            <span>Source Material</span>
            <h2>Upload script and notes</h2>
          </div>
          <label className="dropzone">
            <input
              type="file"
              accept=".pdf,.txt,.text,.fountain,.fdx,.rtf,text/*,application/pdf"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) onScriptUpload(file);
                event.target.value = "";
              }}
            />
            <Icon name="upload" />
            <strong>{scriptUpload ? scriptUpload.fileName : "Choose screenplay file"}</strong>
            <small>{scriptUpload ? `${(scriptUpload.size / 1024).toFixed(0)} KB ready for parsing` : "PDF, FDX, Fountain, RTF, or text"}</small>
          </label>
          <button className="secondary-action full" onClick={onOpenImport} type="button">
            <Icon name="plus" />
            Import character cards, boards, voices
          </button>
        </article>

        <article className="setup-card smart-defaults-card">
          <div className="card-heading">
            <span>Smart Defaults</span>
            <h2>{goalLabel(project.goal)} setup</h2>
          </div>
          <div className="defaults-list">
            <span>{formatRuntime(project.runtimeSeconds)} target</span>
            <span>{project.aspectRatio} frame</span>
            <span>{project.resolution} output test</span>
            <span>{recommendedScenes}</span>
            <span>{recommendedBoards}</span>
          </div>
        </article>
      </section>

      <section className="setup-card goal-panel">
        <div className="card-heading">
          <span>Goal</span>
          <h2>What do you want to make first?</h2>
        </div>
        <div className="use-case-grid">
          {goals.map((goal) => (
            <button className={`use-case-card ${project.goal === goal ? "active" : ""}`} key={goal} onClick={() => onProjectChange({ goal })} type="button">
              <strong>{goalLabel(goal)}</strong>
              <small>{goalCopy[goal]}</small>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
